import { Spinner } from "@fluentui/react"
import { Validate } from "@oneethos/shared"
import { useState } from "react"
import { FaCaretLeft, FaChevronLeft } from "react-icons/fa"
import { FormInput, SubformProps, Validation } from "."
import { AddressInputs } from "../../components/forms"
import { PrequalHelperVariant, usePrequalPasteHelper } from "../../hooks"

const { validateFields } = Validate


type CoborrowerPrequalProps = Partial<SubformProps> & {
  submitCombinedPrequal: (testPrequalResultCombined?: 'pass' | 'fail' | 'no-hit') => void
}

/**
 * This component overlaps heavily with the primary Eligibility component, but there are 
 * enough differences that it's likely best to keep them separate. Regardless, changes
 * to Eligibility component should likely be considered if making chanegs here.
 */
export const CoborrowerPrequal = ({
  formData,
  update,
  schema,
  saving,
  submitCombinedPrequal,
}: CoborrowerPrequalProps) => {
  usePrequalPasteHelper(update, PrequalHelperVariant.CoborrowerEligibility)

  const [validation, setValidation] = useState<Record<string, string>>({})

  const validate = () => {
    return validateFields(schema, formData, [
      'coFirstName',
      'coLastName',
      'coEmail',
      'coBorrowerAddress',
      'coStatedGrossAnnualIncome',
      'coBirthDate'
    ])
  }

  const needsMoreInfo = ['no-hit', 'reject'].includes(formData.prequalResultCombined)

  return (
    <div>
      <div className="buttons text-start mb-3">
        <button
          type="button"
          className="btn btn-link"
          onClick={() => update({
            ...formData,
            includeCoBorrower: 'no',
            declinedToAddCoborrowerDate: undefined
          })}
        ><FaChevronLeft />Back</button>
      </div>
      <h2>Add a Co-Borrower</h2>
      <p>
        Complete the following form to check combined eligibility for yourself plus a co-borrower.
        This will result in a soft credit inquiry for the co-borrower.
      </p>
      <div className="row">
        <div className="col-md-4">
          <FormInput
            field='coFirstName'
            value={formData.coFirstName}
            onChange={s => update('coFirstName', s)}
          />
        </div>
        <div className="col-md-2">
          <FormInput
            field='coMiddleName'
            emptyOk
            value={formData.coMiddleName}
            onChange={s => update('coMiddleName', s)}
          />
        </div>
        <div className="col-md-6">
          <FormInput
            field='coLastName'
            value={formData.coLastName}
            onChange={s => update('coLastName', s)}
          />
        </div>
      </div>
      <FormInput
        field='coEmail'
        value={formData.coEmail || ''}
        onChange={s => update('coEmail', s)}
      />
      <div className="row">
        <div className="col-md-6">
          <FormInput
            field='coStatedGrossAnnualIncome'
            value={formData.coStatedGrossAnnualIncome}
            onChange={s => update('coStatedGrossAnnualIncome', s)}
          />
        </div>
        <div className="col-md-6">
          <FormInput
            field='coBirthDate'
            value={formData.coBirthDate || ''}
            onChange={s => update('coBirthDate', s)}
          />
        </div>
      </div>

      <AddressInputs
        address={formData.coBorrowerAddress || { country: 'United States' }}
        label="Co-Borrower Address"
        onChange={address => update('coBorrowerAddress', address)}
      />

      {needsMoreInfo || formData.coSsn ? <div className="form-group">
        {needsMoreInfo ? <div className="alert alert-danger">
          <div className="mb-2">We couldn't find enough information to verify your credit eligibility.</div>
          <div>
            Please review the information above for accuracy. Please make sure to use
            your full legal name and address. Please also include your social security number to
            ensure access to your credit profile.
          </div>
        </div> : null}
        <FormInput
          field='coSsn'
          value={formData.coSsn || ''}
          onChange={s => update('coSsn', s)}
        />
      </div> : null}

      <button
        type="button"
        className="btn btn-primary"
        disabled={saving}
        onClick={() => {
          const v = validate()
          setValidation(v)
          if (Object.keys(v).length === 0) {
            submitCombinedPrequal()
          }
        }}
      >{saving ? <Spinner /> : 'Check Combined Eligibility'}</button>
      {process.env.REACT_APP_NODE_ENV === 'test' ? <>
        {['pass', 'fail', 'no-hit'].map((test) => <button type="button" key={test}
          className="btn btn-secondary"
          onClick={() => {
            const v = validate()
            setValidation(v)
            if (Object.keys(v).length === 0) {
              submitCombinedPrequal(test as 'pass' | 'fail' | 'no-hit')
            }
          }}
        >Check Eligibility ({test})</button>)}
      </> : null}
      {validation ? (
        <Validation validation={validation} /> || <div className="alert alert-info">Form is valid</div>
      ) : null}

    </div>
  )
}